import { memo, MouseEventHandler, useEffect } from 'react';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import Head from 'next/head';
import LegacyImage from 'next/legacy/image';

import SeedLabsLocale from 'locale/en/seed-labs.json';
import MiscLocale from 'locale/en/misc.json';
import { SliderEl } from 'components/site/site_sections';
import RichTextRenderer from 'components/misc/rich-text-renderer';

import { RootState } from 'entities/rootState';
import cloudinaryLoader from 'lib/image/cloudinary-loader';
import { createGlobalStyle } from 'styled-components';

import {
  mqXXLargeMin,
  mqSmallMin,
  mqLargeMin,
} from 'styled/tools/media-queries';
import {
  AspectRatio,
  AspectRatioObject,
  HideScrollbar,
  WithFontHead,
} from 'styles/commonCss';
import { getContentSection } from 'lib/contentful/get-content-section';
import { getContentField } from 'lib/contentful/get-content-field';
import { handleize } from 'lib/utils/handleize';
import { createMarkup } from 'lib/utils/create-markup';
import { getAuthorImage } from 'lib/utils/get-author-image';
import { getContentfulDataDirect } from 'lib/contentful/get-contentful-data-direct';
import { pageReplaced } from 'lib/utils/page-replaced';

const GlobalStyleSeedLabs = createGlobalStyle`
  .site-content {
    > .container {
      padding-top: 35px;
      @media ${mqLargeMin} {
        padding-top: 165px;
      }
      .swiper-wrapper {
        height: 100% !important;
      }
    }

    * {
      color: var(--text-primary);
    }

    .hero {
      display: block;
    }
    .hero > .container {
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
    }
    .hero .row {
      display: flex;
      flex-direction: column;
      @media ${mqLargeMin} {
        flex-direction: row;
        justify-content: space-between;
        align-items: flex-start;
      }
      width: 100%;
    }
    .hero .title {
      width: 100%;
      margin-bottom: 50px;
      @media ${mqLargeMin} {
        width: 25%;
        margin-bottom: 0;
        font-size: var(--font-size-1200);
      }
    }
    .hero .subtext {
      @media ${mqLargeMin} {
        display: none;
      }
    }
    .hero .title h1 {
      font-size: 26px;
      @media ${mqLargeMin} {
        font-size: var(--font-size-1000);
      }
      @media ${mqXXLargeMin} {
        font-size: var(--font-size-1200);
      }
    }
    .hero .intro {
      display: flex;
      flex-grow: 1;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      width: 100%;
      margin-bottom: 40px;
      @media ${mqLargeMin} {
        width: 75%;
        margin-bottom: 0;
      }
    }
    .hero .intro p {
      ${WithFontHead}
      width: 100%;
      font-size: var(--font-size-400);
      font-weight: 400;
      line-height: 24px;
      @media ${mqLargeMin} {
        max-width: 800px;
        font-size: var(--font-size-800);
        line-height: 40px;
        font-weight: 300;
      }
    }
    .hero .intro p:empty {
      display: none;
    }
    .hero .intro p:not(:first-child) {
      margin-top: var(--spacing-24);
    }
    .hero .intro p:last-child {
      display: block;
    }
    .hero .navi {
      ${HideScrollbar}
      margin: 20px -30px 0;
      padding: 0 var(--spacing-32);
      display: block;
      height: auto;
      overflow-x: auto;
      white-space: nowrap;

      @media ${mqLargeMin} {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        align-items: center;
        margin: 100px 0 0;
        width: 100%;
        white-space: normal;
      }
    }
    .hero .navi li {
      display: inline-block;
      width: 30.25%;
      @media ${mqSmallMin} {
        width: 23.25%;
      }
      @media ${mqLargeMin} {
        width: auto;
      }
    }
    .hero .navi a {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      white-space: normal;
    }
    .hero .navi a span {
      font-size: 13px;
      padding: 0 var(--spacing-8);
      text-align: center;
      font-weight: 500;
      max-width: 80px;
      margin: auto;
      margin-top: 20px;
      @media ${mqLargeMin} {
        font-size: var(--font-size-400);
        max-width: 100%;
      }
    }
    .hero .navi img {
      margin-bottom: var(--spacing-24);
      width: 100%;
      @media ${mqLargeMin} {
        width: 175px;
      }
      @media ${mqXXLargeMin} {
        width: 223px;
      }
    }

    .head > .title {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      flex-wrap: wrap;
      border-top: 1px solid var(--outline-primary);
      margin: 70px 0 15px 0;
      position: relative;

      @media ${mqLargeMin} {
        justify-content: center;
        margin: 110px 0 50px;
        flex-wrap: nowrap;
      }
    }
    .head > .title * {
      ${WithFontHead}
      padding: 10px 0 0;
      font-size: 40px;
      @media ${mqLargeMin} {
        padding: 25px 0 0;
        font-size: 50px;
      }
    }
    .head .label {
      display: block;
      border-bottom: 1px dashed var(--outline-neutral);
      padding: 30px 0 40px;
      margin: 0 0 45px;
      @media ${mqLargeMin} {
        margin: 0 0 35px;
        padding: 0 0 35px;
        display: none;
      }
      &:empty {
        display: none;
      }
    }
    .head .label * {
      ${WithFontHead}
      font-size: 26px;
      line-height: 34px;
    }
    .head .num {
      margin-right: auto;
      @media ${mqLargeMin} {
        display: block;
        position: absolute;
        left: 0;
        top: 0;
      }
    }
    .head .coming-title {
      width: 100%;
      * {
        font-size: 16px;
        font-weight: 400;
        line-height: 24px;
        padding-top: 15px;
      }
      @media ${mqLargeMin} {
        padding-top: 0;
        position: absolute;
        right: 0;
        bottom: 5px;
        width: auto;
      }
    }

    .banner {
      display: block;
    }
    .banner .container {
      position: relative;
    }
    .banner .text {
      display: none;
      @media ${mqLargeMin} {
        display: flex;
        justify-content: center;
        align-items: center;
        position: absolute;
        top: 0;
        right: 0;
        height: 100%;
        padding: 0 var(--spacing-48) 0 0;
      }
    }
    .banner .text p {
      ${WithFontHead}
      font-size: var(--font-size-800);
      line-height: 40px;
      max-width: 100%;
      color: #fff;
      width: 532px;
    }
    .banner .media {
      ${AspectRatio('4x3')}
      @media ${mqLargeMin} {
        padding-bottom: 56.25%;
      }
    }
    .banner .poster {
      ${AspectRatioObject}
      background-size: cover;
    }

    .body {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      align-items: flex-start;
      position: relative;

      @media ${mqLargeMin} {
        margin: 60px 0 0;
      }
    }
    .authors {
      border-bottom: 1px dashed var(--outline-neutral);
      padding: 0 0 40px;
      margin: 0 0 45px;
      width: 100%;
      @media ${mqLargeMin} {
        border: 0;
        width: 25%;
        padding: 0;
        margin-bottom: 0;
        position: absolute;
        left: 0;
        top: 0;
      }
    }
    .authors > .container > .title {
      font-weight: 500;
      margin: 0 0 40px;
      @media ${mqLargeMin} {
        margin: 0 0 35px;
      }
    }
    .authors .author {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      margin: 0 0 14px;
      width: 100%;
      &:last-child {
        margin: 0;
      }
      .text {
        margin-left: 10px;
      }
    }
    .authors .author .text * {
      font-size: var(--font-size-400);
      font-weight: 400;
    }
    .authors .author img {
      margin-right: var(--spacing-8);
      max-width: 70px;
      @media ${mqLargeMin} {
        margin-right: var(--spacing-24);
      }
    }

    .content {
      width: 100%;
    }
    .content > .container {
      @media ${mqLargeMin} {
        max-width: 445px;
        margin: auto;
        width: 100%;
      }
      @media ${mqXXLargeMin} {
        max-width: 645px;
      }
    }
    .content hr {
      border: 0;
      display: block;
      margin: 40px 0 45px;
      border-top: 1px dashed var(--outline-neutral);
      @media ${mqLargeMin} {
        margin: var(--spacing-48) 0;
      }
    }
    .content h1,
    .content h2,
    .content h3,
    .content h4,
    .content h5,
    .content h6 {
      display: block;
      font-weight: 500;
      &:not(:first-child) {
        padding: 35px 0 0;
        @media ${mqLargeMin} {
          padding: var(--spacing-48) 0 0;
        }
      }
    }
    .content h1 {
      font-size: var(--font-size-1200);
    }
    .content h2 {
      font-size: var(--font-size-1000);
    }
    .content h3 {
      font-size: var(--font-size-800);
    }
    .content h4 {
      font-size: var(--font-size-600);
    }
    .content h5 {
      font-size: var(--font-size-400);
    }
    .content h6 {
      font-size: var(--font-size-350);
    }
    .content p {
      font-size: var(--font-size-400);
      &:not(:first-child) {
        padding-top: var(--spacing-24);
      }
      > br:first-child {
        display: none;
      }
    }
    .content small {
      font-size: var(--font-size-300);
      font-weight: 400;
      display: block;
    }
    .content small * {
      text-decoration: none;
      font-size: var(--font-size-300);
    }

    .swiper {
      margin: var(--spacing-48) 0 0;
      @media ${mqLargeMin} {
        margin: var(--spacing-48) 0 0;
        height: 740px;
      }
    }
    .swiper-pagination-bullets {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      height: 5px;
      z-index: 1;
      position: relative;
      margin: 25px 0;
      @media ${mqLargeMin} {
        margin: 0;
        position: absolute;
        bottom: 30px;
        left: 0;
      }
    }
    .swiper-pagination-bullet {
      display: inline-block;
      height: 5px;
      width: 5px;
      margin: 3.5px;
      border-radius: 50%;
      background: var(--fill-neutral-light);
      border: none;
      @media ${mqLargeMin} {
        background: transparent;
        border: 1px solid #fff;
      }
      &:hover,
      &:focus,
      &.swiper-pagination-bullet-active {
        background: var(--fill-brand);
        @media ${mqLargeMin} {
          background: #fff;
        }
      }
    }
    .s-coming-soon {
      .head > .title {
        justify-content: center;
      }
      .head > .title * {
        ${WithFontHead}
        padding: 25px 0 0;
        font-size: 30px;
        @media ${mqLargeMin} {
          font-size: 50px;
        }
      }
    }
  }
`;

const PageData = {
  page_type: 'default',
  page_class: 'page-seed-labs',
  page_name: SeedLabsLocale.page_name,
  page_title: SeedLabsLocale.page_title,
  page_description: SeedLabsLocale.page_desc,
};
const PageHead = (
  <Head>
    <title>{`${MiscLocale.brand} • ${PageData.page_title}`}</title>
    <meta name='description' content={PageData.page_description} />
  </Head>
);

type NaviProps = {
  page: any;
  handleScrollTo: MouseEventHandler<HTMLAnchorElement>;
};

const Navi = memo(({ page, handleScrollTo }: NaviProps) => {
  const sections = getContentSection({
    data: page,
    // TSREVIEW:
    // This was a string but the code expects an array
    exclude: ['Hero'],
  });
  return (
    <ul className='navi'>
      {sections.map((v, i) => {
        const { title } = v.fields;
        const sectionId = handleize(title);
        const image = getContentField({ data: v, type: 'poster' })[0]?.url;
        return (
          <li key={i}>
            <a
              href={`#${sectionId}`}
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
                handleScrollTo(e)
              }
              onKeyDown={(e: React.KeyboardEvent<HTMLAnchorElement>) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const target = document.getElementById(sectionId);
                  if (target) {
                    target.scrollIntoView({ behavior: 'smooth' });
                  }
                }
              }}
            >
              <LegacyImage
                src={image}
                alt={title}
                width={175}
                height={175}
                loader={cloudinaryLoader}
              />
              <span>{title}</span>
            </a>
          </li>
        );
      })}
    </ul>
  );
});

type HeroProps = {
  page: any;
  handleScrollTo: MouseEventHandler<HTMLAnchorElement>;
};

const Hero = memo(({ page, handleScrollTo }: HeroProps) => {
  const section = getContentSection({ data: page, include: 'Hero' })?.shift();

  const title = getContentField({ data: section, type: 'heading' });

  const intro = getContentField({ data: section, type: 'paragraph' });

  const subintro = getContentField({ data: section, title: 'Tap to explore' });

  return (
    <section className='hero'>
      <div className='container'>
        <div className='row'>
          <div
            className='title'
            dangerouslySetInnerHTML={createMarkup(documentToHtmlString(title))}
          />
          <div
            className='intro'
            dangerouslySetInnerHTML={createMarkup(documentToHtmlString(intro))}
          />
          <div
            className='subtext'
            dangerouslySetInnerHTML={createMarkup(
              documentToHtmlString(subintro),
            )}
          />
        </div>

        <Navi page={page} handleScrollTo={handleScrollTo} />
      </div>
    </section>
  );
});

type AuthorsProps = {
  title: string;
  authors: any[];
};

const Authors = memo(({ title, authors }: AuthorsProps) => {
  if (!authors || authors.length <= 0) return null;
  return (
    <div className='authors'>
      <div className='container'>
        <div className='title'>{title}</div>
        <div className='list'>
          {authors.map((author, i) => {
            const data = author.fields;
            const title = data?.title;
            const subtitle = data?.subtitle;
            const img = data?.image['grid-bubble'];
            return (
              <div key={i} className='author'>
                <LegacyImage
                  src={img}
                  alt={title}
                  width={70}
                  height={70}
                  loader={cloudinaryLoader}
                />
                <div className='text'>
                  <div className='title'>{title}</div>
                  <div className='subtitle'>{subtitle}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});
const Section = ({ index, section, collective }) => {
  const title = section?.fields?.title;
  const classes = section?.fields?.class || [];

  const slider = getContentField({ data: section, type: 'slider' });
  const isPagination = slider.slide.length > 1;
  const sliderParams = isPagination
    ? {
        pagination: {
          el: '.swiper-pagination',
          type: 'bullets',
          clickable: true,
        },
      }
    : {};

  const main = getContentField({ data: section, type: 'paragraph' });

  const small = getContentField({ data: section, type: 'small' });

  const tag = getContentField({ data: section, type: 'tag' });
  const authorTitle = tag && tag.title;
  const hasNoContent = classes.includes('coming-soon');
  const authorList =
    (tag && collective.filter((v) => tag?.tag?.indexOf(v.fields.slug) > -1)) ||
    null;

  const head = (
    <div className='head'>
      <h2 className='title'>
        <span className='num'>0{index + 1}</span>
        <span>{title}</span>

        {hasNoContent && (
          <div className='coming-title'>
            {/* TSREVIEW: */}
            {/* @ts-ignore */}
            <RichTextRenderer
              content={getContentField({ data: section, title: 'coming' })}
            />
          </div>
        )}
      </h2>
      <SliderEl title={title} data={slider} params={sliderParams} />
      <div
        className='label'
        dangerouslySetInnerHTML={createMarkup(
          documentToHtmlString(
            getContentField({ data: slider?.slide[0], type: 'paragraph' }),
          ),
        )}
      />
    </div>
  );
  const body = (
    <div className='body'>
      <Authors title={authorTitle} authors={authorList} />
      <div className='content'>
        <div className='container'>
          <div
            dangerouslySetInnerHTML={createMarkup(
              documentToHtmlString(main).replace(/\n/g, '</br>'),
            )}
          />
          <small
            dangerouslySetInnerHTML={createMarkup(
              documentToHtmlString(small).replace(/\n/g, '</br>'),
            )}
          />
        </div>
      </div>
    </div>
  );
  const sectionId = handleize(title);
  return (
    <section className={`s-${handleize(title)}`} id={sectionId}>
      <div className='container'>
        {head}
        {!hasNoContent && body}
      </div>
    </section>
  );
};
const Sections = ({ page, collective }) => {
  const sections = getContentSection({ data: page, exclude: ['Hero'] });
  return (
    <>
      {sections.map((v, i) => (
        <Section section={v} key={i} index={i} collective={collective} />
      ))}
    </>
  );
};

type PageSeedLabsProps = {
  seedlabs: object;
  collective: [object];
} & RootState;

const PageSeedLabs = (props: PageSeedLabsProps) => {
  const { seedlabs, collective } = props;

  useEffect(() => {
    pageReplaced(PageData);
  });

  const handleScrollTo = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const id = e.currentTarget.getAttribute('href')?.replace('#', '');
    if (!id) return;

    const main = document.getElementById(id);
    const head = document.querySelector<HTMLElement>('header[role="banner"]');
    if (!main || !head) return;

    const mainTop = main.offsetTop - head.offsetHeight + 1;
    window.scrollTo({ top: mainTop, left: 0, behavior: 'smooth' });
  };

  return (
    <>
      <GlobalStyleSeedLabs />
      {PageHead}
      {seedlabs && collective && (
        <div className='container'>
          <Hero page={seedlabs} handleScrollTo={handleScrollTo} />
          <Sections page={seedlabs} collective={collective} />
        </div>
      )}
    </>
  );
};

type item = {
  fields: {
    image: any;
    poster: any;
  };
};
export async function getStaticProps() {
  const collectiveQuery = {
    content_type: 'post',
    order: 'fields.date',
    limit: 100,
  };
  const collectivePayload = (await getContentfulDataDirect(collectiveQuery))
    .items;
  // @ts-ignore
  const collectiveList = collectivePayload.map((v: item) => {
    const rand = Math.floor(Math.random() * 14 + 1);
    if (v.fields)
      v.fields.image = {
        'grid-bubble': getAuthorImage(v.fields.poster, 'grid-bubble', rand),
        'grid-default': getAuthorImage(v.fields.poster, 'grid-default', rand),
        popup: getAuthorImage(v.fields.poster, 'popup', rand),
      };
    return { ...v };
  });

  const pageQuery = {
    content_type: 'page',
    'fields.slug[in]': 'seedlabs',
    include: 10,
  };
  const pagePayload = await getContentfulDataDirect(pageQuery);

  const props = {
    seedlabs: pagePayload.items[0].fields,
    collective: collectiveList,
  };

  return {
    props,
    revalidate: 60 * 60, // One hour in seconds
  };
}

export default PageSeedLabs;
